import React, { Component, useState } from 'react'
import axios from 'axios'
import { toast } from 'helperLibraries/index'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import createBuildingActionCreator from 'redux/actions/createBuilding'
import fetchCurrentUserActionCreator from 'redux/actions/fetchCurrentUser'
import PropTypes from 'prop-types'
import ResiDeskIconWhiteOrange from '../../Assets/resideskIconWhiteOrange-01.svg'
import Loading from 'components/common/LoadingGif'
import { Card } from '../ui/card'
import * as yup from 'yup'
import { motion } from 'motion/react'
import PasswordReset from '../PasswordReset'
import { AuroraBackground } from '../ui/aurora'

// Input component with consistent styling
const Input = ({ label, type = "text", ...props }) => (
  <div className="space-y-2.5">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <input
      type={type}
      className="w-full h-11 px-4 rounded-lg border border-gray-200 
                 transition-all duration-200 placeholder:text-gray-400
                 focus:outline-none focus:ring-2 focus:ring-theme-primary/20 focus:border-theme-primary"
      {...props}
    />
  </div>
);

// Button component with theme colors
const Button = ({ children, variant = "primary", ...props }) => {
  const baseStyles = "w-full h-11 rounded-lg font-medium transition-all duration-200";
  const variants = {
    primary: "bg-theme-primary text-white hover:bg-theme-primary/90 disabled:opacity-50",
    secondary: "bg-gray-100 text-theme-primary hover:bg-gray-200 disabled:opacity-50"
  };
  
  return (
    <button 
      className={`${baseStyles} ${variants[variant]}`}
      {...props}
    >
      {children}
    </button>
  );
};

// Password reset form
const PasswordResetForm = ({ onBack, isResetting, setIsResetting }) => {
  const [email, setEmail] = useState('');

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setIsResetting(true);

    try {
      await axios.post('/api/reset-password/request-reset', { email });
      toast.success('Check your email for password reset instructions');
      onBack();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to request password reset');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Reset Password</h1>
        <p className="text-sm text-gray-500">Enter your email to receive reset instructions</p>
      </div>
      
      <form onSubmit={handlePasswordReset} className="space-y-5">
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isResetting}
          required
          placeholder="name@example.com"
        />
        
        <div className="space-y-3">
          <Button 
            type="submit"
            disabled={isResetting || !email}
          >
            {isResetting ? 'Sending...' : 'Send Reset Link'}
          </Button>
          <Button
            variant="secondary"
            onClick={onBack}
            type="button"
            disabled={isResetting}
          >
            Back to Sign In
          </Button>
        </div>
      </form>
    </div>
  );
};

// Login form component
const LoginForm = ({ emailText, passwordText, onChangeInputText, loginUser, onForgotPassword }) => (
  <div className="space-y-8">
    <div className="space-y-2 text-center">
      <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Welcome back</h1>
      <p className="text-sm text-gray-500">Sign in to your account</p>
    </div>

    <div className="space-y-5">
      <Input
        id="username"
        name="username"
        label="Email"
        type="email"
        value={emailText}
        data-cy="login-email"
        onChange={(e) => onChangeInputText('emailText')(null, e.target.value)}
        autoComplete="username"
        placeholder="name@example.com"
      />
      
      <Input
        id="password"
        name="password"
        label="Password"
        type="password"
        value={passwordText}
        data-cy="login-password"
        onChange={(e) => onChangeInputText('passwordText')(null, e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && loginUser()}
        autoComplete="current-password"
        placeholder="Enter your password"
      />

      <div className="space-y-4">
        <Button
          data-cy="login-submit"
          disabled={!emailText || !passwordText}
          onClick={loginUser}
        >
          Sign In
        </Button>

        <button 
          onClick={onForgotPassword}
          className="w-full text-sm text-theme-primary hover:underline"
        >
          Forgot your password?
        </button>
      </div>
    </div>
  </div>
);

// Main sign in container
const SignInContainer = ({ children }) => (
  <AuroraBackground className="px-4 py-12">
    <motion.div 
      className="w-full max-w-sm space-y-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex justify-center">
        <div className="flex items-center gap-3">
          <img
            src={ResiDeskIconWhiteOrange}
            className="h-12 w-12 relative [filter:invert(1)_brightness(0.3)]"
            alt="ResiDesk Logo"
          />
          <span className="text-2xl font-semibold [filter:brightness(0.3)] tracking-tight">ResiDesk</span>
        </div>
      </div>
      <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-2xl border-white/20">
        {children}
      </Card>
    </motion.div>
  </AuroraBackground>
);

// Form validation schema
const loginSchema = yup.object().shape({
  email: yup
    .string()
    .email('Please enter a valid email')
    .required('Please enter a valid email')
    .label('Email'),
  password: yup
    .string()
    .required('Please enter your password')
    .label('Password'),
});

const validateForm = (formValues) => {
  try {
    return loginSchema.validateSync(formValues, { abortEarly: false });
  } catch (error) {
    error.inner.forEach(err => toast.error(err.message));
    return false;
  }
};

class SignIn extends Component {
  state = {
    emailText: '',
    passwordText: '',
    showResetForm: false,
    isResetting: false,
  }

  onChangeInputText = (inputKey) => (_, inputValue) =>
    this.setState({ [inputKey]: inputValue })

  loginUser = async () => {
    const { emailText, passwordText } = this.state;
    const { history } = this.props;

    const credentials = {
      email: emailText,
      password: passwordText,
    };

    if (!validateForm(credentials)) return;

    try {
      await axios.post('/api/auth/login', credentials, {
        headers: { Pragma: 'no-cache' },
      });

      toast.success(`Successfully signed in as ${emailText}`);
      this.props.fetchCurrentUser(history);
    } catch (error) {
      if (error.response?.status === 403 && error.response.data?.error === 'Password reset required') {
        toast.error(error.response.data.message);
        return;
      }
      
      const resetToken = error.response?.data?.resetToken;
      
      if (resetToken) {
        toast.info('Please complete your password reset to continue');
        history.push(`/reset-password?token=${resetToken}`);
        return;
      }
      
      toast.error(error.response?.data?.message || 'There was an error signing you in to your account');
    }
  };

  toggleResetForm = () => {
    this.setState(state => ({ showResetForm: !state.showResetForm }));
  }

  setIsResetting = (isResetting) => {
    this.setState({ isResetting });
  }

  render() {
    const { loader: { isLoad }, location } = this.props;
    const { emailText, passwordText, showResetForm, isResetting } = this.state;

    if (isLoad) return <Loading />;

    if (location.pathname === '/reset-password') {
      return <PasswordReset />;
    }

    return (
      <SignInContainer>
        {showResetForm ? (
          <PasswordResetForm
            onBack={this.toggleResetForm}
            isResetting={isResetting}
            setIsResetting={this.setIsResetting}
          />
        ) : (
          <LoginForm
            emailText={emailText}
            passwordText={passwordText}
            onChangeInputText={this.onChangeInputText}
            loginUser={this.loginUser}
            onForgotPassword={this.toggleResetForm}
          />
        )}
      </SignInContainer>
    );
  }
}

SignIn.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  loader: PropTypes.object.isRequired,
}

const mapStateToProps = ({ quickStart, auth, loader }) => ({
  quickStart,
  auth,
  loader,
})

const mapDispatchToProps = {
  createBuilding: createBuildingActionCreator,
  fetchCurrentUser: fetchCurrentUserActionCreator,
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SignIn)
)
